import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/api";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { items, open, setOpen, updateQuantity, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#FAF8F5] border-l border-[#E6DFD3] w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-[#E6DFD3]">
          <SheetTitle className="font-serif text-2xl text-[#2D372B] text-left">Your Basket</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <ShoppingBag className="w-10 h-10 text-[#B85C38]" />
            <p className="text-[#7C897B]">Your basket is empty.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {items.map((i) => (
              <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                <img src={i.image} alt={i.name} className="w-20 h-20 object-cover rounded-sm border border-[#E6DFD3]" />
                <div className="flex-1">
                  <p className="font-sans font-medium text-[#2D372B] leading-tight">{i.name}</p>
                  {i.variant && <p className="text-xs text-[#7C897B] mt-0.5">{i.variant}</p>}
                  <p className="text-sm text-[#B85C38] mt-1">{formatPrice(i.unit_price)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      data-testid={`decrease-${i.product_id}`}
                      onClick={() => updateQuantity(i.key, i.quantity - 1)}
                      className="w-7 h-7 border border-[#E6DFD3] flex items-center justify-center hover:bg-[#F0EBE1]"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center text-sm">{i.quantity}</span>
                    <button
                      data-testid={`increase-${i.product_id}`}
                      onClick={() => updateQuantity(i.key, i.quantity + 1)}
                      className="w-7 h-7 border border-[#E6DFD3] flex items-center justify-center hover:bg-[#F0EBE1]"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <button
                  data-testid={`remove-${i.product_id}`}
                  onClick={() => removeItem(i.key)}
                  className="text-[#7C897B] hover:text-[#B85C38] self-start"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-[#E6DFD3] px-6 py-5 space-y-4">
            <div className="flex justify-between text-[#2D372B]">
              <span className="font-sans">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-serif text-xl">{formatPrice(subtotal)}</span>
            </div>
            <p className="text-xs text-[#7C897B]">Shipping calculated at checkout.</p>
            <button
              data-testid="checkout-button"
              onClick={goCheckout}
              className="w-full bg-[#B85C38] hover:bg-[#9A4A2B] text-white uppercase tracking-widest text-sm py-3.5 transition-colors rounded-sm"
            >
              Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
